/**
 * Editor для doc.customer_card_issue (Порція Роздріб.5b).
 * Простий документ — save / post / unpost / delete.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailCustomerCardIssues } from '@/api/retailCustomerCardIssue';
import { retailCustomerCards } from '@/api/retailCustomerCards';
import { retailPosWorkstations } from '@/api/retailPosWorkstations';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';

const PERMISSION = 'retail.customer_card.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_card_id: string;
  pos_workstation_id: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  customer_card_id: '',
  pos_workstation_id: '',
  comment: '',
};

export default function CustomerCardIssueEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const cardsQ = useQuery({
    queryKey: ['retail-customer-cards-for-issue', form.organization_id],
    queryFn: () => retailCustomerCards.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const wsQ = useQuery({
    queryKey: ['retail-pos-workstations-for-issue', form.organization_id],
    queryFn: () => retailPosWorkstations.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-customer-card-issue', id],
    queryFn: () => retailCustomerCardIssues.get(id!),
    enabled: !isNew,
  });

  const numberPreview = useNumberPreview('doc.customer_card_issue', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customer_card_id: d.customer_card_id,
        pos_workstation_id: d.pos_workstation_id ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const posted = !!docQuery.data?.posted;

  const create = useMutation({
    mutationFn: () =>
      retailCustomerCardIssues.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        customer_card_id: form.customer_card_id,
        pos_workstation_id: form.pos_workstation_id || null,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issues'] });
      navigate(`/retail/customer-card-issue/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailCustomerCardIssues.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        pos_workstation_id: form.pos_workstation_id || null,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issues'] });
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issue', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Новий: створити та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await retailCustomerCardIssues.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        customer_card_id: form.customer_card_id,
        pos_workstation_id: form.pos_workstation_id || null,
        comment: form.comment || null,
      });
      await retailCustomerCardIssues.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issues'] });
      navigate(`/retail/customer-card-issue/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Збережений: зберегти правки та провести одним кліком (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await retailCustomerCardIssues.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        pos_workstation_id: form.pos_workstation_id || null,
        comment: form.comment || null,
      });
      await retailCustomerCardIssues.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issues'] });
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issue', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => retailCustomerCardIssues.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issues'] });
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issue', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailCustomerCardIssues.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-customer-card-issues'] });
      navigate('/retail/customer-card-issue');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  function validate(): boolean {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        customer_card_id: form.customer_card_id,
      },
      {
        organization_id: 'Юрособа',
        customer_card_id: 'Картка клієнта',
      },
    );
    if (err) { setError(err); return false; }
    return true;
  }

  const handleSave = () => {
    if (!validate()) return;
    if (isNew) create.mutate();
    else update.mutate();
  };
  // Новий: зберегти та провести.
  const handleSaveAndPost = () => {
    if (!validate()) return;
    createAndPost.mutate();
  };
  // Збережений: зберегти правки та провести.
  const handleUpdateAndPost = () => {
    if (!validate()) return;
    updateAndPost.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Нова видача картки ${numberPreview ? `(${numberPreview})` : ''}`
            : `Видача картки ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/customer-card-issue')}>Скасувати</button>
          {!posted && (
            <RequirePermission code={PERMISSION}>
              <button
                className="primary"
                onClick={handleSave}
                disabled={create.isPending || update.isPending || createAndPost.isPending}
              >
                Зберегти
              </button>
            </RequirePermission>
          )}
          {isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="primary"
                onClick={handleSaveAndPost}
                disabled={create.isPending || createAndPost.isPending}
                title="Зберігає та проводить документ"
              >
                ▶ Провести
              </button>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code={PERMISSION}>
              <button
                className="primary"
                onClick={handleUpdateAndPost}
                disabled={updateAndPost.isPending || update.isPending}
                title="Зберігає поточні зміни та проводить документ"
              >
                ▶ Провести
              </button>
            </RequirePermission>
          )}
          {!isNew && posted && (
            <RequirePermission code={PERMISSION}>
              <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
                ◀ Скасувати проводку
              </button>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити документ?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <label>Дата документу</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={posted}
          />
        </div>
        <div className="field">
          <RequiredLabel>Картка клієнта</RequiredLabel>
          <select
            value={form.customer_card_id}
            onChange={(e) => setForm({ ...form, customer_card_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {cardsQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.card_code} — {c.holder_name ?? '(без імені)'}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>POS-станція (опц.)</label>
          <select
            value={form.pos_workstation_id}
            onChange={(e) => setForm({ ...form, pos_workstation_id: e.target.value })}
            disabled={posted}
          >
            <option value="">—</option>
            {wsQ.data?.items.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
            disabled={posted}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
